import { useState } from "react";
import { ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERSONAS = [
    { id: "bank", label: "Bank / NBFC" },
    { id: "buyer", label: "Large Buyer / OEM" },
    { id: "msme", label: "Exporting MSME" },
    { id: "regulator", label: "Regulator" },
    { id: "other", label: "Other" }
];

export default function ContactForm() {
    const [form, setForm] = useState({ name: "", organization: "", email: "", persona: "bank", message: "" });
    const [status, setStatus] = useState({ state: "idle", msg: "" });

    const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.organization || !form.email || !form.message) {
            setStatus({ state: "error", msg: "Please fill every field before submitting." });
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            setStatus({ state: "error", msg: "That email address does not look right." });
            return;
        }
        setStatus({ state: "sending", msg: "" });
        try {
            await axios.post(`${API}/leads`, { ...form, source: "carbonis.in · contact form" });
            setStatus({ state: "success", msg: "Thank you — a Carbonis strategist will reach out within one business day." });
            setForm({ name: "", organization: "", email: "", persona: "bank", message: "" });
        } catch (err) {
            const detail = err?.response?.data?.detail || "Something went wrong. Please try email — info@carbonis.in.";
            setStatus({ state: "error", msg: typeof detail === "string" ? detail : "Please try email — info@carbonis.in." });
        }
    };

    return (
        <form
            onSubmit={submit}
            className="bg-white border border-navy/10 p-8 lg:p-10 flex flex-col gap-6"
            data-testid="contact-form"
            noValidate
        >
            <div className="flex items-center gap-4">
                <div className="w-10 h-px bg-gold"></div>
                <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-gold">
                    Or Send a Briefing Request
                </span>
            </div>

            <h3 className="font-serif text-3xl lg:text-4xl leading-[1.05] tracking-[-0.01em] text-navy">
                Tell us the seat you sit in.
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Full Name" testid="contact-form-name">
                    <input
                        type="text"
                        value={form.name}
                        onChange={setField("name")}
                        className="input"
                        data-testid="contact-form-name-input"
                        autoComplete="name"
                        maxLength={120}
                    />
                </Field>
                <Field label="Organization" testid="contact-form-org">
                    <input
                        type="text"
                        value={form.organization}
                        onChange={setField("organization")}
                        className="input"
                        data-testid="contact-form-org-input"
                        autoComplete="organization"
                        maxLength={180}
                    />
                </Field>
                <Field label="Email" testid="contact-form-email">
                    <input
                        type="email"
                        value={form.email}
                        onChange={setField("email")}
                        className="input"
                        data-testid="contact-form-email-input"
                        autoComplete="email"
                        maxLength={180}
                    />
                </Field>
                <Field label="I am a" testid="contact-form-persona">
                    <select
                        value={form.persona}
                        onChange={setField("persona")}
                        className="input"
                        data-testid="contact-form-persona-select"
                    >
                        {PERSONAS.map((p) => (
                            <option key={p.id} value={p.id}>{p.label}</option>
                        ))}
                    </select>
                </Field>
            </div>

            <Field label="What you are exploring" testid="contact-form-message">
                <textarea
                    value={form.message}
                    onChange={setField("message")}
                    rows={5}
                    className="input resize-none"
                    data-testid="contact-form-message-input"
                    placeholder="Portfolio review, CBAM readiness, regulator sandbox pilot…"
                    maxLength={4000}
                />
            </Field>

            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 pt-2">
                <button
                    type="submit"
                    className="btn-institution disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={status.state === "sending"}
                    data-testid="contact-form-submit"
                >
                    {status.state === "sending" ? "Sending…" : "Request briefing"}
                    <ArrowRight size={14} strokeWidth={1.5} />
                </button>
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-navy/50">
                    Responded to within one business day
                </span>
            </div>

            {status.state === "success" && (
                <div
                    className="flex items-start gap-3 border-t border-navy/10 pt-5 text-navy"
                    data-testid="contact-form-success"
                    role="status"
                >
                    <CheckCircle2 size={18} className="text-gold mt-0.5" strokeWidth={1.5} />
                    <span className="text-[14px] leading-relaxed">{status.msg}</span>
                </div>
            )}
            {status.state === "error" && (
                <div
                    className="flex items-start gap-3 border-t border-navy/10 pt-5 text-navy"
                    data-testid="contact-form-error"
                    role="alert"
                >
                    <AlertCircle size={18} className="text-gold mt-0.5" strokeWidth={1.5} />
                    <span className="text-[14px] leading-relaxed">{status.msg}</span>
                </div>
            )}
        </form>
    );
}

function Field({ label, children, testid }) {
    return (
        <label className="flex flex-col gap-2" data-testid={testid}>
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/50">{label}</span>
            {children}
        </label>
    );
}
